import { useState, useEffect, useRef } from 'react';
import { Input } from '../../../ui';
import { DescuentosPromocionalesProps } from '@/types';
import { safeFetch } from '@/api/safeFetch';

interface Props extends DescuentosPromocionalesProps {
  profesionId?: number;
  onMensajeChange?: (mensaje: string | null) => void;
  onCodigoValido?: (codigo: string | null) => void;
  onAfiliadoDetectado?: (afiliado: any | null) => void;
}

const DescuentosPromocionales: React.FC<Props> = ({
  profesionId,
  onDescuentoChange,
  onMensajeChange,
  onCodigoValido,
  onAfiliadoDetectado,
  descuentoHabilitado = false,
}) => {
  const [codigoDescuento, setCodigoDescuento] = useState('');
 const [, setMensaje] = useState<string | null>(null);

  const lastCheckedRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const codigo = codigoDescuento.trim().toUpperCase();
    const timeoutId = setTimeout(async () => {
      if (!codigo) {
        setMensaje(null);
        onMensajeChange?.(null);
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
        lastCheckedRef.current = null;
        return;
      }

      if (codigo.length < 4) return;

      if (!descuentoHabilitado) {
        setMensaje('El descuento solo está disponible para pago anual');
        onMensajeChange?.('El descuento solo está disponible para pago anual');
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
        return;
      }

      if (!profesionId) {
        setMensaje('Esperando la profesión seleccionada...');
        onMensajeChange?.('Esperando la profesión seleccionada...');
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        return;
      }

      const comprobacion = `${codigo}:${profesionId}`;
      if (lastCheckedRef.current === comprobacion) return;
      lastCheckedRef.current = comprobacion;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await safeFetch(
          '/api/codigos-promocionales/validar-profesion',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              codigo,
              profesionId,
            }),
            signal: controller.signal,
          }
        );

        const data = await res.json();

        if (!res.ok || !data.valid) {
          const error = data.message || data.mensaje ||
            'El código no es válido para la profesión seleccionada';

          setMensaje(error);
          onMensajeChange?.(error);
          onDescuentoChange?.(0);
          onCodigoValido?.(null);
          onAfiliadoDetectado?.(null);
          return;
        }

        onAfiliadoDetectado?.(data.afiliado ?? null);

        if (data.afiliado && data.afiliado.activo !== 1) {
          const error = 'Afiliado inactivo';
          setMensaje(error);
          onMensajeChange?.(error);
          onDescuentoChange?.(0);
          onCodigoValido?.(null);
          onAfiliadoDetectado?.(null);
          return;
        }

        setMensaje(null);
        onMensajeChange?.(null);
        onDescuentoChange?.(Number(data.porcentaje_descuento) || 0);
        onCodigoValido?.(codigo);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;

        console.error('Error validando código promocional:', err);
        setMensaje('Error del servidor al validar el código');
        onMensajeChange?.('Error del servidor al validar el código');
        onDescuentoChange?.(0);
        onCodigoValido?.(null);
        onAfiliadoDetectado?.(null);
      }
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [
    codigoDescuento,
    descuentoHabilitado,
    profesionId,
    onDescuentoChange,
    onMensajeChange,
    onCodigoValido,
    onAfiliadoDetectado,
  ]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return (
    <div className="descuentos-promocionales">
      <Input
        type="text"
        placeholder={
          descuentoHabilitado
            ? 'Código de descuento'
            : 'Descuento solo para pago anual'
        }
        value={codigoDescuento}
        onChange={(e) => {
          setCodigoDescuento(e.target.value.toUpperCase());
          lastCheckedRef.current = null;
          setMensaje(null);
          onMensajeChange?.(null);
          onDescuentoChange?.(0);
          onCodigoValido?.(null);
          onAfiliadoDetectado?.(null);
        }}
        className="w-full bg-blue-200 rounded-lg border border-blue-700 text-center"
        disabled={!descuentoHabilitado}
      />
    </div>
  );
};

export default DescuentosPromocionales;